import { createReducer, on } from '@ngrx/store';
import { RightsManagementState } from '../../models/user-management.models';
import * as UserManagementActions from '../actions/user-management.actions';

export const initialState: RightsManagementState = {
    users: null,
    roles: null,
    assignedRights: null,
    selectedUser: null,
    selectedRole: null,
    loading: false,
    error: null,
    searchTerm: '',
    filters: {
        roleFilter: [],
    },
};

export const userManagementReducer = createReducer(
    initialState,

    // Load Users
    on(UserManagementActions.loadUsers, state => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(UserManagementActions.loadUsersSuccess, (state, { users }) => ({
        ...state,
        users,
        loading: false,
    })),
    on(UserManagementActions.loadUsersFailure, (state, { error }) => ({
        ...state,
        error,
        loading: false,
    })),

    // Load Roles
    on(UserManagementActions.loadRoles, state => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(UserManagementActions.loadRolesSuccess, (state, { roles }) => ({
        ...state,
        roles,
        loading: false,
    })),
    on(UserManagementActions.loadRolesFailure, (state, { error }) => ({
        ...state,
        error,
        loading: false,
    })),

    // Load Rights
    on(UserManagementActions.loadRights, state => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(UserManagementActions.loadRightsSuccess, (state, { assignedRights }) => ({
        ...state,
        assignedRights,
        loading: false,
    })),
    on(UserManagementActions.loadRightsFailure, (state, { error }) => ({
        ...state,
        error,
        loading: false,
    })),

    // Load User Rights
    on(UserManagementActions.loadUserRights, state => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(UserManagementActions.loadUserRightsSuccess, (state, { rights }) => ({
        ...state,
        selectedUser: state.selectedUser
            ? {
                  ...state.selectedUser,
                  rights: [...rights],
              }
            : null,
        loading: false,
    })),
    on(UserManagementActions.loadUserRightsFailure, (state, { error }) => ({
        ...state,
        error,
        loading: false,
    })),

    // Create Role
    on(UserManagementActions.createRole, state => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(UserManagementActions.createRoleSuccess, (state, { role }) => ({
        ...state,
        roles: state.roles.some(r => r.id === role.id) ? state.roles.map(r => (r.id === role.id ? role : r)) : [...state.roles, role],
        loading: false,
    })),
    on(UserManagementActions.createRoleFailure, (state, { error }) => ({
        ...state,
        error,
        loading: false,
    })),

    // Update Role
    on(UserManagementActions.updateRole, state => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(UserManagementActions.updateRoleSuccess, (state, { role }) => ({
        ...state,
        roles: state.roles.map(r => (r.id === role.id ? role : r)),
        loading: false,
    })),
    on(UserManagementActions.updateRoleFailure, (state, { error }) => ({
        ...state,
        error,
        loading: false,
    })),

    // Delete Role
    on(UserManagementActions.deleteRole, state => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(UserManagementActions.deleteRoleSuccess, (state, { roleId }) => ({
        ...state,
        roles: state.roles.filter(r => r.id !== roleId),
        loading: false,
    })),
    on(UserManagementActions.deleteRoleFailure, (state, { error }) => ({
        ...state,
        error,
        loading: false,
    })),

    // Update User Rights
    on(UserManagementActions.updateUserRights, state => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(UserManagementActions.updateUserRightsSuccess, (state, { rights }) => ({
        ...state,
        selectedUser: state.selectedUser
            ? {
                  ...state.selectedUser,
                  rights: [...rights],
              }
            : null,
        loading: false,
    })),
    on(UserManagementActions.updateUserRightsFailure, (state, { error }) => ({
        ...state,
        error,
        loading: false,
    })),

    // UI Actions
    on(UserManagementActions.setSelectedUser, (state, { user }) => ({
        ...state,
        selectedUser: user,
    })),

    on(UserManagementActions.setSelectedRole, (state, { role }) => ({
        ...state,
        selectedRole: role,
    })),

    on(UserManagementActions.setSearchTerm, (state, { searchTerm }) => ({
        ...state,
        searchTerm,
    })),

    on(UserManagementActions.setRoleFilter, (state, { roleIds }) => ({
        ...state,
        filters: {
            ...state.filters,
            roleFilter: roleIds,
        },
    })),

    on(UserManagementActions.setStatusFilter, (state, { status }) => ({
        ...state,
        filters: {
            ...state.filters,
            statusFilter: status,
        },
    })),

    on(UserManagementActions.clearFilters, state => ({
        ...state,
        searchTerm: '',
        filters: {
            roleFilter: [],
            statusFilter: 'all' as const,
        },
    })),

    // Clear State
    on(UserManagementActions.clearUserManagementState, () => initialState)
);
